import { ContentPlanningProject, ContentPlanningPage, KeywordResearchProject, ServicePageKeywordMapping } from '../types';
import { cloudSyncService } from './cloudSyncService';

const CONTENT_PLANNING_PROJECTS_KEY = 'zynex_ninja_content_planning_projects_v1';
const ACTIVE_CONTENT_PLANNING_PROJECT_KEY = 'zynex_ninja_active_content_planning_project_id';

export class ContentPlanningStorageService {
  /**
   * Helper function to extract and convert finalized keyword research mappings & variations
   * into ContentPlanningPage items.
   */
  static getVariationsForKw(
    kwProject: KeywordResearchProject,
    targetKw: string,
    tier?: 'Primary' | 'Secondary' | 'Tertiary',
    isHomepage: boolean = false
  ): string[] {
    if (!targetKw || !targetKw.trim() || !kwProject) return [];
    const cleanTarget = targetKw.trim().toLowerCase();
    const set = new Set<string>();

    const normalizeForParentMatch = (str: string): string => {
      return (str || '')
        .replace(/^variation:\s*/i, '')
        .replace(/[^a-z0-9\s]/gi, ' ')
        .replace(/\b(in|and|for|the|of|to|a|an|near|me|&)\b/gi, ' ')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
    };

    const targetNorm = normalizeForParentMatch(cleanTarget);

    const isParentMatch = (parentStr: string): boolean => {
      if (!parentStr || !parentStr.trim()) return false;
      const cleanParent = parentStr.trim().toLowerCase();
      if (cleanParent === cleanTarget) return true;
      const parentNorm = normalizeForParentMatch(cleanParent);
      if (parentNorm === targetNorm && parentNorm.length > 0) return true;
      return false;
    };

    // From kwProject.variations
    if (Array.isArray(kwProject.variations)) {
      kwProject.variations.forEach(v => {
        if (!v.keyword) return;
        const kwLower = v.keyword.trim().toLowerCase();
        if (cleanTarget && kwLower === cleanTarget) return;

        const varParent = v.variationOf || '';
        const matchesParent = isParentMatch(varParent);
        const isStep10TierMatch = isHomepage && tier && (
          v.targetTier === tier ||
          (v.step10Assigned === true && v.targetTier === tier) ||
          (v.reason && v.reason.includes(`${tier} Target`))
        );

        if (matchesParent || isStep10TierMatch) {
          set.add(v.keyword.trim());
        }
      });
    }

    // From kwProject.masterKeywords
    if (Array.isArray(kwProject.masterKeywords)) {
      kwProject.masterKeywords.forEach(mk => {
        if (!mk.keyword) return;
        const kwLower = mk.keyword.trim().toLowerCase();
        if (cleanTarget && kwLower === cleanTarget) return;

        const mkParent = mk.variationOf || '';
        const matchesParent = isParentMatch(mkParent);
        const isStep10TierMatch = isHomepage && tier && (
          mk.priority === tier && mk.type === 'Variation'
        );

        if (matchesParent || isStep10TierMatch) {
          set.add(mk.keyword.trim());
        }
      });
    }

    return Array.from(set);
  }

  static getServicePageVariations(
    kwProject: KeywordResearchProject,
    spm: ServicePageKeywordMapping,
    tier: 'Primary' | 'Secondary' | 'Tertiary',
    targetKw?: string
  ): string[] {
    const set = new Set<string>();

    const mainKw = (targetKw || (tier === 'Primary' ? (spm.primaryKeyword || '') : tier === 'Secondary' ? (spm.secondaryKeyword || '') : (spm.tertiaryKeyword || ''))).trim();
    const secondaryKw = (tier === 'Primary' ? (spm.primaryKeyword2 || '') : tier === 'Secondary' ? (spm.secondaryKeyword2 || '') : (spm.tertiaryKeyword2 || '')).trim();

    if (secondaryKw && secondaryKw.toLowerCase() !== mainKw.toLowerCase()) {
      set.add(secondaryKw);
    }

    if (mainKw) {
      this.getVariationsForKw(kwProject, mainKw, tier, false).forEach(v => set.add(v));
    }
    if (secondaryKw) {
      this.getVariationsForKw(kwProject, secondaryKw, tier, false).forEach(v => set.add(v));
    }

    const candidatePool = spm.candidatePool || [];
    const supporting = spm.supportingKeywords || [];

    const pageCandidates = Array.from(new Set([
      ...supporting,
      ...candidatePool.map((c: any) => c.keyword)
    ])).filter(Boolean);

    // Distribute candidate items per tier so each card gets 2-4 distinct targeted variations
    pageCandidates.forEach((cand, idx) => {
      const cClean = cand.trim();
      const cLower = cClean.toLowerCase();
      if (!cLower || cLower === mainKw.toLowerCase() || cLower === secondaryKw.toLowerCase()) return;

      if (tier === 'Primary' && (idx % 3 === 0 || cLower.includes('genuine') || cLower.includes('supplier'))) {
        set.add(cClean);
      } else if (tier === 'Secondary' && (idx % 3 === 1 || cLower.includes('oem') || cLower.includes('fleet'))) {
        set.add(cClean);
      } else if (tier === 'Tertiary' && (idx % 3 === 2 || cLower.includes('spare') || cLower.includes('parts'))) {
        set.add(cClean);
      }
    });

    return Array.from(set);
  }

  static importPagesFromKeywordProject(kwProject: KeywordResearchProject): ContentPlanningPage[] {
    const pages: ContentPlanningPage[] = [];

    // 1. Build Homepage entry from Step 10
    const masterPrimaries = (kwProject.masterKeywords || []).filter(m => m.priority === 'Primary' && m.status !== 'rejected');
    const masterSecondaries = (kwProject.masterKeywords || []).filter(m => m.priority === 'Secondary' && m.status !== 'rejected');
    const masterTertiaries = (kwProject.masterKeywords || []).filter(m => m.priority === 'Tertiary' && m.status !== 'rejected');

    let hpPrimary = masterPrimaries.length > 0 ? masterPrimaries[0].keyword : '';
    let hpSecondary = masterSecondaries.length > 0 ? masterSecondaries[0].keyword : '';
    let hpTertiary = masterTertiaries.length > 0 ? masterTertiaries[0].keyword : '';

    if (!hpPrimary) {
      const seeds = kwProject.seedKeywords || [];
      if (seeds.length > 0) hpPrimary = seeds[0].seedKeyword;
      if (!hpSecondary && seeds.length > 1) hpSecondary = seeds[1].seedKeyword;
      if (!hpTertiary && seeds.length > 2) hpTertiary = seeds[2].seedKeyword;
    }

    const hpPrimaryVars = this.getVariationsForKw(kwProject, hpPrimary, 'Primary', true);
    const hpSecondaryVars = this.getVariationsForKw(kwProject, hpSecondary, 'Secondary', true);
    const hpTertiaryVars = this.getVariationsForKw(kwProject, hpTertiary, 'Tertiary', true);

    pages.push({
      id: `cp-page-home-${Date.now()}`,
      pageName: `Homepage - ${kwProject.clientName || 'Main Hub'}`,
      targetUrl: kwProject.websiteUrl || '/',
      pageType: 'Homepage',
      primaryKeyword: hpPrimary,
      primaryKeywordVariations: hpPrimaryVars,
      secondaryKeyword: hpSecondary,
      secondaryKeywordVariations: hpSecondaryVars,
      tertiaryKeyword: hpTertiary,
      tertiaryKeywordVariations: hpTertiaryVars,
      notes: kwProject.homepageSeoTitle ? `Homepage SEO Title: ${kwProject.homepageSeoTitle}` : 'Imported Homepage keyword research mapping',
      status: 'planned',
    });

    // 2. Build Service Pages from Step 11 servicePageMappings
    if (kwProject.servicePageMappings && kwProject.servicePageMappings.length > 0) {
      kwProject.servicePageMappings.forEach((spm, idx) => {
        if (spm.pageName.toLowerCase().includes('home') || spm.targetUrl === '/') return;

        const primVars = this.getServicePageVariations(kwProject, spm, 'Primary');
        const secVars = this.getServicePageVariations(kwProject, spm, 'Secondary');
        const tertVars = this.getServicePageVariations(kwProject, spm, 'Tertiary');

        pages.push({
          id: `cp-page-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 4)}`,
          pageName: spm.pageName || `Service Page #${idx + 1}`,
          targetUrl: spm.targetUrl || `/services/page-${idx + 1}`,
          pageType: 'Service Page',
          primaryKeyword: spm.primaryKeyword || '',
          primaryKeywordVariations: primVars,
          secondaryKeyword: spm.secondaryKeyword || '',
          secondaryKeywordVariations: secVars,
          tertiaryKeyword: spm.tertiaryKeyword || '',
          tertiaryKeywordVariations: tertVars,
          notes: 'Imported from Keyword Research Step 11 Service Mappings',
          status: 'planned',
        });
      });
    }

    return pages;
  }

  static syncProjectVariationsWithKeywordProject(
    cpProject: ContentPlanningProject,
    kwProject: KeywordResearchProject
  ): ContentPlanningProject {
    if (!kwProject || !cpProject.pages) return cpProject;

    const masterPrimaries = (kwProject.masterKeywords || []).filter(m => m.priority === 'Primary' && m.status !== 'rejected');
    const masterSecondaries = (kwProject.masterKeywords || []).filter(m => m.priority === 'Secondary' && m.status !== 'rejected');
    const masterTertiaries = (kwProject.masterKeywords || []).filter(m => m.priority === 'Tertiary' && m.status !== 'rejected');

    const hpPrimaryMaster = masterPrimaries.length > 0 ? masterPrimaries[0].keyword : '';
    const hpSecondaryMaster = masterSecondaries.length > 0 ? masterSecondaries[0].keyword : '';
    const hpTertiaryMaster = masterTertiaries.length > 0 ? masterTertiaries[0].keyword : '';

    const servicePages = kwProject.servicePageMappings || [];
    let serviceIdx = 0;
    let hasChanges = false;

    const updatedPages = cpProject.pages.map(page => {
      const isHomepage = page.pageType === 'Homepage' || page.targetUrl === '/' || page.pageName.toLowerCase().includes('home');

      let primKw = page.primaryKeyword;
      let secKw = page.secondaryKeyword;
      let tertKw = page.tertiaryKeyword;

      let primVars: string[] = [];
      let secVars: string[] = [];
      let tertVars: string[] = [];

      if (isHomepage) {
        if (hpPrimaryMaster && (!primKw || primKw !== hpPrimaryMaster)) primKw = hpPrimaryMaster;
        if (hpSecondaryMaster && (!secKw || secKw !== hpSecondaryMaster)) secKw = hpSecondaryMaster;
        if (hpTertiaryMaster && (!tertKw || tertKw !== hpTertiaryMaster)) tertKw = hpTertiaryMaster;

        primVars = this.getVariationsForKw(kwProject, primKw, 'Primary', true);
        secVars = this.getVariationsForKw(kwProject, secKw, 'Secondary', true);
        tertVars = this.getVariationsForKw(kwProject, tertKw, 'Tertiary', true);
      } else {
        const spm = servicePages.find(s =>
          s.pageName.toLowerCase().trim() === page.pageName.toLowerCase().trim() ||
          s.targetUrl.toLowerCase().trim() === page.targetUrl.toLowerCase().trim()
        ) || servicePages[serviceIdx++];

        if (spm) {
          if (!primKw && spm.primaryKeyword) primKw = spm.primaryKeyword;
          if (!secKw && spm.secondaryKeyword) secKw = spm.secondaryKeyword;
          if (!tertKw && spm.tertiaryKeyword) tertKw = spm.tertiaryKeyword;

          primVars = this.getServicePageVariations(kwProject, spm, 'Primary', primKw);
          secVars = this.getServicePageVariations(kwProject, spm, 'Secondary', secKw);
          tertVars = this.getServicePageVariations(kwProject, spm, 'Tertiary', tertKw);
        } else {
          primVars = this.getVariationsForKw(kwProject, primKw, 'Primary', false);
          secVars = this.getVariationsForKw(kwProject, secKw, 'Secondary', false);
          tertVars = this.getVariationsForKw(kwProject, tertKw, 'Tertiary', false);
        }
      }

      const currentPrim = page.primaryKeywordVariations || [];
      const currentSec = page.secondaryKeywordVariations || [];
      const currentTert = page.tertiaryKeywordVariations || [];

      if (
        page.primaryKeyword !== primKw ||
        page.secondaryKeyword !== secKw ||
        page.tertiaryKeyword !== tertKw ||
        JSON.stringify(currentPrim) !== JSON.stringify(primVars) ||
        JSON.stringify(currentSec) !== JSON.stringify(secVars) ||
        JSON.stringify(currentTert) !== JSON.stringify(tertVars)
      ) {
        hasChanges = true;
        return {
          ...page,
          primaryKeyword: primKw,
          primaryKeywordVariations: primVars,
          secondaryKeyword: secKw,
          secondaryKeywordVariations: secVars,
          tertiaryKeyword: tertKw,
          tertiaryKeywordVariations: tertVars,
        };
      }

      return page;
    });

    if (hasChanges) {
      const updated = {
        ...cpProject,
        pages: updatedPages,
        updatedAt: new Date().toISOString(),
      };
      this.saveProject(updated);
      return updated;
    }

    return cpProject;
  }

  static createUnitedTruckingSample(): ContentPlanningProject {
    return {
      id: 'cp-project-united-trucking',
      clientName: 'United Trucking Supplies',
      websiteUrl: 'https://unitedtruckingsupplies.com',
      targetedLocation: 'Trinidad and Tobago',
      category: 'Automotive & Heavy Duty Truck Spare Parts',
      linkedAuditId: 'audit_united_trucking',
      linkedKeywordProjectId: 'kw-project-united-trucking',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'in_progress',
      notes: 'Imported finalized keyword research project for Trinidad commercial fleet market.',
      pages: [
        {
          id: 'cp-ut-p1',
          pageName: 'Homepage - Heavy Duty Truck Parts Trinidad Hub',
          targetUrl: 'https://unitedtruckingsupplies.com/',
          pageType: 'Homepage',
          primaryKeyword: 'auto parts trinidad',
          primaryKeywordVariations: ['trinidad auto parts', 'Auto Parts in Trinidad and Tobago', 'auto parts place in trinidad'],
          secondaryKeyword: 'truck parts in trinidad',
          secondaryKeywordVariations: ['truck parts trinidad', 'truck parts for sale in trinidad', 'Truck Parts Trinidad and Tobago'],
          tertiaryKeyword: 'bus parts trinidad',
          tertiaryKeywordVariations: ['bus fleet spare parts trinidad', 'commercial bus parts'],
          status: 'in_progress',
          notes: 'Main commercial landing page targeting top search volume queries in Trinidad.',
          competitorHeadings: [
            {
              id: 'comp-ut-1',
              competitorNumber: 1,
              nameOrUrl: 'Trinidad Truck Spares (https://trinidadtruckspares.com)',
              headings: [
                'H1: Premier Heavy Duty Truck & Auto Parts Supplier Trinidad',
                'H2: Genuine OEM Truck Engine Components',
                'H2: Heavy Duty Suspension & Brake Parts',
                'H3: Commercial Fleet Delivery Across Port of Spain & Chaguanas',
              ],
            },
            {
              id: 'comp-ut-2',
              competitorNumber: 2,
              nameOrUrl: 'Caribbean Fleet Motors (https://caribbeanfleetmotors.com)',
              headings: [
                'H1: Commercial Truck & Bus Spare Parts Trinidad',
                'H2: Wholesale Auto Parts & Hydraulic Seals',
                'H3: Emergency Fleet Breakdown Support',
              ],
            },
          ],
          painPoints: [
            {
              id: 'pain-ut-1',
              title: 'Long shipping delays for heavy-duty truck replacement parts',
              description: 'Commercial fleet owners lose revenue when trucks sit idle waiting 3-4 weeks for imported engine components.',
            },
            {
              id: 'pain-ut-2',
              title: 'Counterfeit or low-grade aftermarket parts causing frequent breakdowns',
              description: 'Mechanics struggle with sub-standard spare parts that fail under heavy tropical road conditions.',
            },
            {
              id: 'pain-ut-3',
              title: 'Lack of local inventory transparency and instant stock check',
              description: 'Drivers have to physically visit multiple shops in Port of Spain to verify if brake drums or filters are in stock.',
            },
          ],
        },
        {
          id: 'cp-ut-p2',
          pageName: 'Auto Parts Category Hub',
          targetUrl: 'https://unitedtruckingsupplies.com/auto-parts/',
          pageType: 'Category Hub',
          primaryKeyword: 'auto parts trinidad',
          primaryKeywordVariations: ['auto parts store trinidad', 'car replacement parts trinidad'],
          secondaryKeyword: 'bus parts trinidad',
          secondaryKeywordVariations: ['bus fleet spare parts trinidad', 'commercial bus parts'],
          tertiaryKeyword: 'brake & suspension replacement parts',
          tertiaryKeywordVariations: ['truck brake pads trinidad', 'suspension springs trinidad'],
          status: 'planned',
          notes: 'High volume consumer and fleet auto parts hub.',
          competitorHeadings: [
            {
              id: 'comp-ut-2-1',
              competitorNumber: 1,
              nameOrUrl: 'Island Auto Supplies Trinidad',
              headings: [
                'H1: Auto Parts & Accessories Store Trinidad',
                'H2: Shop Brake Systems, Filters & Batteries',
              ],
            },
          ],
          painPoints: [
            {
              id: 'pain-ut-2-1',
              title: 'Uncertainty regarding vehicle compatibility and exact model fitment',
              description: 'Car owners fear purchasing incorrect replacement parts that do not fit their exact trim or year model.',
            },
          ],
        },
        {
          id: 'cp-ut-p3',
          pageName: 'Commercial Fleet Maintenance & Parts',
          targetUrl: 'https://unitedtruckingsupplies.com/commercial-fleet/',
          pageType: 'Service Page',
          primaryKeyword: 'commercial fleet spare parts',
          primaryKeywordVariations: ['fleet maintenance parts trinidad', 'heavy duty fleet parts'],
          secondaryKeyword: 'heavy duty truck parts trinidad',
          secondaryKeywordVariations: ['diesel truck engine parts trinidad'],
          tertiaryKeyword: 'truck parts supplier port of spain',
          tertiaryKeywordVariations: ['truck parts store port of spain'],
          status: 'planned',
          notes: 'Targeting commercial B2B fleet logistics buyers.',
          painPoints: [
            {
              id: 'pain-ut-3-1',
              title: 'High upfront cost without bulk B2B commercial fleet discounts',
              description: 'Logistics firms need credit terms or volume pricing for ongoing fleet maintenance contracts.',
            },
          ],
        },
      ],
    };
  }

  static createReflectWindowSample(): ContentPlanningProject {
    return {
      id: 'cp-project-reflect-window',
      clientName: 'Reflect Window Cleaning',
      websiteUrl: 'https://reflectwindowcleaning.com',
      targetedLocation: 'Edmonton, AB',
      category: 'Residential & Commercial Window Cleaning, Gutter Cleaning & Pressure Washing',
      linkedAuditId: 'audit_reflect_window',
      linkedKeywordProjectId: 'kw-project-reflect-window',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'in_progress',
      notes: 'Content plan focused on residential window cleaning, commercial washing, and Edmonton local gutter cleaning leads.',
      pages: [
        {
          id: 'cp-rw-p1',
          pageName: 'Homepage - Reflect Window Cleaning Edmonton Hub',
          targetUrl: 'https://reflectwindowcleaning.com/',
          pageType: 'Homepage',
          primaryKeyword: 'window cleaning edmonton',
          primaryKeywordVariations: ['window washers edmonton', 'best window cleaning edmonton', 'exterior window cleaning edmonton'],
          secondaryKeyword: 'residential window cleaning',
          secondaryKeywordVariations: ['residential window cleaning edmonton', 'home window washers'],
          tertiaryKeyword: 'gutter cleaning edmonton',
          tertiaryKeywordVariations: ['gutter cleaning services edmonton', 'eavestrough cleaning edmonton'],
          status: 'in_progress',
          notes: 'Main local service homepage targeting top window cleaning searches in Edmonton.',
          competitorHeadings: [
            {
              id: 'comp-rw-1',
              competitorNumber: 1,
              nameOrUrl: 'Edmonton Pure Windows (https://edmontonpurewindows.ca)',
              headings: [
                'H1: Premier Window Cleaning & Exterior Property Washing Edmonton',
                'H2: Residential Streak-Free Window Washing',
                'H2: Commercial & Storefront Window Care',
                'H3: Gutter Cleanout & Downspout Flushing',
              ],
            },
          ],
          painPoints: [
            {
              id: 'pain-rw-1',
              title: 'Streaks, smudges, and hard water stains left behind by amateur cleaners',
              description: 'Homeowners struggle to achieve crystal-clear, streak-free window panes on multi-story houses without professional de-ionized water equipment.',
            },
            {
              id: 'pain-rw-2',
              title: 'Safety hazards of climbing ladders for second-story windows and gutters',
              description: 'Residents risk injury attempting to clean high exterior windows or clogged eavestroughs themselves.',
            },
          ],
        },
        {
          id: 'cp-rw-p2',
          pageName: 'Residential Window Cleaning Service Page',
          targetUrl: 'https://reflectwindowcleaning.com/residential-window-cleaning/',
          pageType: 'Service Page',
          primaryKeyword: 'residential window cleaning',
          primaryKeywordVariations: ['home window cleaning edmonton', 'interior exterior window washers'],
          secondaryKeyword: 'commercial window cleaning edmonton',
          secondaryKeywordVariations: ['storefront window cleaning', 'office window washing'],
          tertiaryKeyword: 'screen & track cleaning edmonton',
          tertiaryKeywordVariations: ['window frame cleaning', 'storm window washing'],
          status: 'planned',
          notes: 'High conversion residential service landing page.',
          painPoints: [
            {
              id: 'pain-rw-2-1',
              title: 'Damage to window screens and landscaping from harsh chemical cleaners',
              description: 'Homeowners worry about toxic solutions damaging garden plants or degrading window seals.',
            },
          ],
        },
        {
          id: 'cp-rw-p3',
          pageName: 'Gutter Cleaning & Pressure Washing Page',
          targetUrl: 'https://reflectwindowcleaning.com/gutter-cleaning-edmonton/',
          pageType: 'Service Page',
          primaryKeyword: 'gutter cleaning edmonton',
          primaryKeywordVariations: ['eavestrough cleaning edmonton', 'gutter cleanout service'],
          secondaryKeyword: 'pressure washing edmonton',
          secondaryKeywordVariations: ['driveway pressure washing', 'siding power wash edmonton'],
          tertiaryKeyword: 'solar panel cleaning edmonton',
          tertiaryKeywordVariations: ['solar array washing', 'residential solar maintenance'],
          status: 'planned',
          notes: 'Seasonal spring/autumn high-demand service hub.',
          painPoints: [
            {
              id: 'pain-rw-3-1',
              title: 'Clogged downspouts causing water overflow and foundation damage',
              description: 'Neglected eavestroughs cause roof leaks and basement moisture damage during heavy storms.',
            },
          ],
        },
      ],
    };
  }

  static getAllProjects(): ContentPlanningProject[] {
    try {
      const raw = localStorage.getItem(CONTENT_PLANNING_PROJECTS_KEY);
      let list: ContentPlanningProject[] = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) list = [];

      // Migrate any legacy "Reflect Window & Door" to "Reflect Window Cleaning"
      let needsSave = false;
      list = list.map(p => {
        if (p.clientName?.includes('Door') || p.websiteUrl?.includes('reflectwindow.com')) {
          needsSave = true;
          return this.createReflectWindowSample();
        }
        return p;
      });

      // Seed default sample projects if list is empty
      if (list.length === 0) {
        const ut = this.createUnitedTruckingSample();
        const rw = this.createReflectWindowSample();
        list = [ut, rw];
        needsSave = true;
      }

      if (needsSave) {
        localStorage.setItem(CONTENT_PLANNING_PROJECTS_KEY, JSON.stringify(list));
      }

      return list;
    } catch {
      return [this.createUnitedTruckingSample(), this.createReflectWindowSample()];
    }
  }

  static getProjectById(id: string): ContentPlanningProject | null {
    const list = this.getAllProjects();
    return list.find(p => p.id === id) || null;
  }

  static getActiveProjectId(): string | null {
    return localStorage.getItem(ACTIVE_CONTENT_PLANNING_PROJECT_KEY);
  }

  static setActiveProjectId(id: string): void {
    localStorage.setItem(ACTIVE_CONTENT_PLANNING_PROJECT_KEY, id);
  }

  static createProject(
    clientName: string,
    websiteUrl: string,
    targetedLocation: string,
    category: string,
    linkedAuditId?: string,
    linkedKeywordProjectId?: string,
    initialPages: ContentPlanningPage[] = []
  ): ContentPlanningProject {
    const projects = this.getAllProjects();

    const newProject: ContentPlanningProject = {
      id: `cp-project-${Date.now()}`,
      clientName,
      websiteUrl,
      targetedLocation: targetedLocation || 'Global / Remote',
      category: category || 'General Business',
      linkedAuditId,
      linkedKeywordProjectId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      pages: initialPages,
      status: 'in_progress',
    };

    projects.unshift(newProject);
    localStorage.setItem(CONTENT_PLANNING_PROJECTS_KEY, JSON.stringify(projects));
    this.setActiveProjectId(newProject.id);
    return newProject;
  }

  static saveProject(project: ContentPlanningProject): void {
    const projects = this.getAllProjects();
    const updatedProject = {
      ...project,
      updatedAt: new Date().toISOString(),
    };

    const idx = projects.findIndex(p => p.id === project.id);
    if (idx !== -1) {
      projects[idx] = updatedProject;
    } else {
      projects.unshift(updatedProject);
    }

    localStorage.setItem(CONTENT_PLANNING_PROJECTS_KEY, JSON.stringify(projects));
    cloudSyncService.saveContentPlanToCloud(updatedProject).catch(() => {});
  }

  static deleteProject(id: string): void {
    const projects = this.getAllProjects().filter(p => p.id !== id);
    localStorage.setItem(CONTENT_PLANNING_PROJECTS_KEY, JSON.stringify(projects));
    if (this.getActiveProjectId() === id) {
      localStorage.removeItem(ACTIVE_CONTENT_PLANNING_PROJECT_KEY);
    }
  }
}
